import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMessages } from '../contexts/MessagesContext';
import { useToast } from './Toast';
interface SupportMessageModalProps {
  onClose: () => void;
  onSubmit: (data: {
    subject: string;
    message: string;
  }) => void;
}
export function SupportMessageModal({
  onClose,
  onSubmit
}: SupportMessageModalProps) {
  const [form, setForm] = useState({
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const {
    addConversation
  } = useMessages();
  const {
    showToast
  } = useToast();
  const handleSubmit = () => {
    if (form.subject && form.message) {
      setIsSubmitting(true);
      // Simulate API call with setTimeout
      setTimeout(() => {
        // Create a new conversation for the support message
        const newConversation = {
          id: Date.now().toString(),
          type: 'support' as const,
          title: form.subject,
          preview: form.message.substring(0, 50) + (form.message.length > 50 ? '...' : ''),
          status: 'waiting' as const,
          date: 'Şimdi',
          unread: true,
          recipient: {
            id: 'support-team',
            name: 'Eğitim Pilotu Destek',
            role: 'Destek Ekibi'
          },
          messages: [{
            id: 'm1',
            sender: 'student' as const,
            content: form.message,
            timestamp: `Bugün, ${new Date().getHours().toString().padStart(2, '0')}:${new Date().getMinutes().toString().padStart(2, '0')}`
          }]
        };
        // Add the new conversation
        addConversation(newConversation);
        // Call the onSubmit callback
        onSubmit(form);
        // Show success toast notification
        showToast('✅ Mesajınız alınmıştır, en kısa sürede dönüş yapılacaktır.', 'success');
        // Close the modal
        onClose();
        // Navigate to Messages page
        navigate('/messages');
        setIsSubmitting(false);
      }, 1000); // Simulate network delay
    }
  };
  return <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50 overflow-y-auto" data-id="element-1068">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-xl" data-id="element-1069">
        <div className="relative bg-black/90 rounded-xl" data-id="element-1070">
          <div className="p-4 border-b border-gray-800 flex justify-between items-center" data-id="element-1071">
            <h2 className="text-xl font-bold" data-id="element-1072">
              Rehberlik Birimine Mesaj Gönder
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white" disabled={isSubmitting} data-id="element-1073">
              <X className="w-6 h-6" data-id="element-1074" />
            </button>
          </div>
          <div className="p-6 space-y-6" data-id="element-1075">
            <div className="space-y-2" data-id="element-1076">
              <label className="text-sm font-medium text-gray-300" data-id="element-1077">
                Konu Seçimi
              </label>
              <div className="relative" data-id="element-1078">
                <select value={form.subject} onChange={e => setForm({
                ...form,
                subject: e.target.value
              })} className="w-full appearance-none bg-white/5 text-white rounded-lg px-4 py-3 focus:ring-2 ring-purple-500/20" disabled={isSubmitting} data-id="element-1079">
                  <option value="" data-id="element-1080">Seçiniz</option>
                  <option value="Teknik Sorunlar" data-id="element-1081">Teknik Sorunlar</option>
                  <option value="Ders İçerikleri Hakkında" data-id="element-1082">
                    Ders İçerikleri Hakkında
                  </option>
                  <option value="Motivasyon ve Stres" data-id="element-1083">
                    Motivasyon ve Stres
                  </option>
                  <option value="Programım Hakkında Soru" data-id="element-1084">
                    Programım Hakkında Soru
                  </option>
                  <option value="Diğer" data-id="element-1085">Diğer</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400" data-id="element-1086">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" data-id="element-1087">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" data-id="element-1088" />
                  </svg>
                </div>
              </div>
              <style jsx data-id="element-1089">{`
                select option {
                  background-color: #1a1a1a;
                  color: white;
                }
                select option:hover,
                select option:focus,
                select option:active {
                  background-color: #6d28d9;
                }
              `}</style>
            </div>
            <div className="space-y-2" data-id="element-1090">
              <label className="text-sm font-medium text-gray-300" data-id="element-1091">
                Mesajınız
              </label>
              <textarea value={form.message} onChange={e => setForm({
              ...form,
              message: e.target.value
            })} className="w-full bg-white/5 text-white rounded-lg px-4 py-3 min-h-[180px] focus:ring-2 ring-purple-500/20" placeholder="Mesajınızı buraya yazın..." disabled={isSubmitting} data-id="element-1092" />
            </div>
          </div>
          <div className="p-4 border-t border-gray-800 flex justify-end gap-3" data-id="element-1093">
            <button onClick={onClose} className="px-4 py-2 bg-transparent border border-gray-700 hover:bg-white/5 text-gray-300 rounded-lg transition-colors" disabled={isSubmitting} data-id="element-1094">
              İptal
            </button>
            <button onClick={handleSubmit} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors flex items-center gap-2" disabled={isSubmitting || !form.subject || !form.message.trim()} data-id="element-1095">
              {isSubmitting ? <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1096">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1097"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1098"></path>
                  </svg>
                  Gönderiliyor...
                </> : <>
                  <Send className="w-4 h-4" data-id="element-1099" />
                  Gönder
                </>}
            </button>
          </div>
        </div>
      </div>
    </div>;
}